
import { useState } from 'react';

interface ImageGalleryProps {
  images: string[];
  title: string;
}

/**
 * ImageGallery component
 *
 * Displays a main image with navigation controls, a thumbnail grid,
 * and an optional lightbox modal.
 *
 * The component now includes defensive checks and error handling to
 * avoid crashes when the `images` array is empty or undefined.
 */
export const ImageGallery = ({ images, title }: ImageGalleryProps) => {
  // Guard against an empty images array
  const safeImages = Array.isArray(images) ? images : [];

  const [selectedImage, setSelectedImage] = useState(0);
  const [showLightbox, setShowLightbox] = useState(false);

  // Ensure selected index is always within bounds
  const safeIndex = (idx: number) => {
    if (safeImages.length === 0) return 0;
    return ((idx % safeImages.length) + safeImages.length) % safeImages.length;
  };

  const handlePrevious = () => {
    setSelectedImage((prev) => safeIndex(prev - 1));
  };

  const handleNext = () => {
    setSelectedImage((prev) => safeIndex(prev + 1));
  };

  // If there are no images, render a simple placeholder
  if (safeImages.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-8 text-center text-gray-500">
        No images to display.
      </div>
    );
  }

  return (
    <>
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {/* Main Image */}
        <div className="relative w-full h-[500px] bg-gray-100">
          <img
            src={safeImages[selectedImage]}
            alt={`${title} - Image ${selectedImage + 1}`}
            className="w-full h-full object-cover object-top cursor-pointer"
            onClick={() => setShowLightbox(true)}
          />

          {/* Navigation Arrows */}
          <button
            onClick={handlePrevious}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-white/90 hover:bg-white rounded-full shadow-lg transition-all cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-2xl text-gray-800"></i>
          </button>
          <button
            onClick={handleNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-white/90 hover:bg-white rounded-full shadow-lg transition-all cursor-pointer"
          >
            <i className="ri-arrow-right-s-line text-2xl text-gray-800"></i>
          </button>

          {/* Image Counter */}
          <div className="absolute bottom-4 right-4 bg-black/70 text-white px-4 py-2 rounded-lg text-sm font-medium">
            {selectedImage + 1} / {safeImages.length}
          </div>

          {/* View All Button */}
          <button
            onClick={() => setShowLightbox(true)}
            className="absolute bottom-4 left-4 bg-white hover:bg-gray-50 text-gray-800 px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap flex items-center space-x-2"
          >
            <i className="ri-gallery-line"></i>
            <span>View All Photos</span>
          </button>
        </div>

        {/* Thumbnail Grid */}
        <div className="p-4 grid grid-cols-6 gap-2">
          {safeImages.slice(0, 6).map((image, index) => (
            <div
              key={index}
              onClick={() => setSelectedImage(index)}
              className={`relative w-full h-20 rounded-lg overflow-hidden cursor-pointer transition-all ${
                selectedImage === index ? 'ring-4 ring-coral-500' : 'hover:opacity-80'
              }`}
            >
              <img
                src={image}
                alt={`Thumbnail ${index + 1}`}
                className="w-full h-full object-cover object-top"
              />
              {index === 5 && safeImages.length > 6 && (
                <div className="absolute inset-0 bg-black/60 flex items-center justify-center text-white font-bold text-sm">
                  +{safeImages.length - 6}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Lightbox Modal */}
      {showLightbox && (
        <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center">
          <button
            onClick={() => setShowLightbox(false)}
            className="absolute top-4 right-4 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>

          <button
            onClick={handlePrevious}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-14 h-14 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-3xl"></i>
          </button>

          <div className="max-w-6xl max-h-[90vh] w-full h-full flex items-center justify-center px-20">
            <img
              src={safeImages[selectedImage]}
              alt={`${title} - Image ${selectedImage + 1}`}
              className="max-w-full max-h-full object-contain"
            />
          </div>

          <button
            onClick={handleNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-14 h-14 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors cursor-pointer"
          >
            <i className="ri-arrow-right-s-line text-3xl"></i>
          </button>

          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white text-lg font-medium">
            {selectedImage + 1} / {safeImages.length}
          </div>
        </div>
      )}
    </>
  );
};
